import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { GroupEntity, GroupEntityCategoryEnum } from '@interpals/api-web';

import { fetchGroups, fetchUserGroups, GROUPS_PAGE_LIMIT } from 'redux/groups/actions';
import { groupSubscribe, groupUnsubscribe } from 'redux/group/actions';

export type GroupOrder = 'members' | 'posts' | 'last_activity' | 'created';
export type GroupTab = 'all' | 'my';
export const DEFAULT_GROUP_TAB: GroupTab = 'all';

export interface GroupSearchFilters {
    keywords?: string;
    order?: GroupOrder;
    category?: GroupEntityCategoryEnum;
    language?: string;
    tab?: GroupTab;
}

export interface GroupsState {
    groups: GroupEntity[];
    hasMore: boolean;
    isLoading: boolean;
    apiError?: number;
}

const initialState: GroupsState = {
    groups: [],
    hasMore: true,
    isLoading: false,
};

const groupListSlice = createSlice({
    name: 'groups',
    initialState,
    reducers: {
        setGroupApiError: (state, action: PayloadAction<number | undefined>) => {
            state.apiError = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            isAnyOf(fetchGroups.fulfilled, fetchUserGroups.fulfilled),
            (state: GroupsState, { payload: { data: groups }, meta }) => {
                if (meta.arg.offset === 0) {
                    state.groups = groups;
                } else {
                    state.groups = [...state.groups, ...groups];
                }

                state.hasMore = groups.length === GROUPS_PAGE_LIMIT;
                state.isLoading = false;
            },
        );

        builder.addMatcher(isAnyOf(fetchGroups.rejected, fetchUserGroups.rejected), () => initialState);

        builder.addMatcher(
            isAnyOf(groupSubscribe.fulfilled, groupUnsubscribe.fulfilled),
            (state: GroupsState, { payload }) => {
                const targetIndex = state.groups.findIndex((group) => group.id === payload.id);
                state.groups[targetIndex] = payload;
            },
        );

        builder.addMatcher(isAnyOf(fetchGroups.pending, fetchUserGroups.pending), (state: GroupsState) => {
            state.isLoading = true;
        });
    },
});

export const { setGroupApiError } = groupListSlice.actions;
export default groupListSlice.reducer;
